/**
 * The item catalog, downloaded once and searched locally.
 *
 * Every item the app shows already exists in the browser's reach — it was just
 * being asked for one request at a time. That cost dominated the Supabase egress
 * bill (see docs/guide/egress-plan.md): a search per keystroke, and a full copy
 * of sixteen items inside every saved build.
 *
 * So the catalog ships as one artifact instead: ~0.46 MB gzipped for all 3,745
 * items and 519 sets, in five languages. The browser already downloads a 617 KB
 * solver kernel, so this is the smaller half of what it fetches to work offline.
 *
 * Loading mirrors the kernel's: read `manifest.json` (never cached) for the
 * current version, then fetch `catalog.<version>.json` (immutable for a year).
 * The digest in the filename is the version — a client re-downloads only when
 * the game data actually changes, which is a few times a year.
 */

const MANIFEST_URL = '/catalog/manifest.json';
/** Give up rather than hang the UI; the API fallback is always there. */
const LOAD_TIMEOUT_MS = 15000;

let loadPromise = null;
let catalog = null;

function indexById(rows, key = 'id') {
    const map = new Map();
    for (const row of rows) map.set(row[key], row);
    return map;
}

async function fetchJson(url, signal, init = {}) {
    const response = await fetch(url, { signal, credentials: 'omit', ...init });
    if (!response.ok) throw new Error(`${url} -> HTTP ${response.status}`);
    return response.json();
}

async function fetchCatalog() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
    try {
        // Revalidated, never served from cache — the same `no-cache` the kernel
        // manifest uses. It had been a plain fetch, which the docstring above
        // already described as uncached and was not: a browser holding the old
        // manifest kept asking for a `catalog.<version>.json` that a repack has
        // pruned, so the catalog 404'd and search silently fell back to the API
        // and to whatever data that server carries.
        const manifest = await fetchJson(MANIFEST_URL, controller.signal, { cache: 'no-cache' });
        if (!manifest?.file || !manifest?.version) {
            throw new Error('catalog manifest is missing file/version');
        }
        const body = await fetchJson(`/catalog/${manifest.file}`, controller.signal);
        const items = Array.isArray(body?.items) ? body.items : [];
        const sets = Array.isArray(body?.sets) ? body.sets : [];
        if (!items.length) throw new Error('catalog has no items');
        return {
            version: body.version || manifest.version,
            format: body.format,
            items,
            sets,
            itemById: indexById(items),
            setById: indexById(sets),
        };
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Load the catalog once per page. Resolves to null rather than throwing when it
 * cannot be had — callers fall back to the API, exactly as the browser solver
 * falls back to the server one.
 */
export function loadItemCatalog() {
    if (catalog) return Promise.resolve(catalog);
    if (!loadPromise) {
        loadPromise = fetchCatalog()
            .then((loaded) => {
                catalog = loaded;
                return catalog;
            })
            .catch((error) => {
                console.warn('[catalog] unavailable, using the API:', error?.message || error);
                // Cleared so a later navigation can retry; a transient failure
                // should not disable local search for the whole session.
                loadPromise = null;
                return null;
            });
    }
    return loadPromise;
}

/**
 * Start loading without waiting, so the first search does not pay for it.
 *
 * Loading is lazy — nothing fetches the catalog until something searches — but
 * lazy alone made the *first* search slower than the API call it replaced
 * (~410 ms to fetch and parse, against ~100 ms over the network). Warming it
 * when the generator mounts moves that cost off the interaction entirely: by the
 * time a slot is opened it is already in memory.
 *
 * Deliberately on idle. The catalog competes with the solver kernel and the
 * page's own assets for the same connection, and nothing here is urgent — a
 * search arriving before the warm-up finishes simply awaits the same promise.
 */
export function prefetchItemCatalog() {
    if (catalog || loadPromise) return;
    const start = () => { loadItemCatalog(); };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 3000 });
    } else {
        setTimeout(start, 0);
    }
}

/** The catalog if it is already in memory, else null. Never triggers a fetch. */
export function peekItemCatalog() {
    return catalog;
}

/** Version of the loaded catalog, for staleness checks against the server. */
export function catalogVersion() {
    return catalog?.version || null;
}

export function resetItemCatalogForTests() {
    catalog = null;
    loadPromise = null;
}
